import { CmsSectionShell } from './CmsSectionShell.tsx';
import { CmsSectionState } from './CmsSectionState.tsx';
import { SectionHeading } from '../SectionHeading.tsx';
import { themeOf } from './cmsTheme.ts';
import { LineupNames } from '../LineupNames.tsx';
import { useFestivalData } from '../../lib/festivalData.ts';
import { getArtistGroups } from '../../application/festivalSelectors.ts';
import type { HeroLineupSection } from '../../lib/types.ts';

/**
 * Front-page lineup strip (CMS-039). The editor picks a year, an optional heading, and which
 * list to show (artists or features — heroLineup has no "both" option, unlike the program section).
 *
 * - 'currentYear': dumb consumer of DataContext (program.json) — same live, already-published
 *   source as CmsArtistsSection. No archive fetching.
 * - 'lastYear': there is no other published feed with historical data, so the names come
 *   straight from the `lineup` field BackOffice baked into this section own JSON at publish
 *   time — no data fetching at all.
 *
 * The names sit at the v2 line-up size (30/44px); a features list runs smaller and muted, since
 * it supplements the poster rather than being it.
 */
export function CmsHeroLineup({ theme, guitar, heading, year, list = 'artists', lineup }: HeroLineupSection) {
  const t = themeOf(theme);
  const showFeatures = list === 'features';
  const namesClass = showFeatures
    ? `font-display text-xl md:text-2xl leading-[1.3] tracking-[-0.01em] ${t.body}`
    : `font-display text-[30px] md:text-[44px] leading-[1.05] tracking-[-0.02em] ${t.heading}`;
  const { data, loading, error } = useFestivalData();

  if (year === 'lastYear') {
    // Last year names are baked in with no id — there's no Program page to link them to.
    const items = (lineup ?? []).map(a => ({ name: a.name }));
    return (
      <CmsSectionShell theme={theme} guitar={guitar}>
        {heading && <SectionHeading color={t.heading} eyebrowColor={t.eyebrow}>{heading}</SectionHeading>}
        <LineupNames items={items} className={namesClass} />
      </CmsSectionShell>
    );
  }

  const groups = data ? getArtistGroups(data) : null;
  const source = (showFeatures ? groups?.features : groups?.artists) ?? [];
  const items = source.map(a => ({ name: a.name, id: a.id }));

  return (
    <CmsSectionShell theme={theme} guitar={guitar}>
      <CmsSectionState
        loading={loading}
        error={!!error}
        loadingLabel="Indlæser line-up…"
        bodyClass={t.body}
        errorMessage="Line-up kunne ikke indlæses."
      >
        {heading && <SectionHeading color={t.heading} eyebrowColor={t.eyebrow}>{heading}</SectionHeading>}
        <LineupNames
          items={items}
          className={namesClass}
          emptyLabel={showFeatures ? 'Oplevelser annonceres snart' : 'Navnene annonceres snart'}
        />
      </CmsSectionState>
    </CmsSectionShell>
  );
}
